import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { formatDateForAPI } from "../../Rajasthan/Utility/const";
import MainPageContainer from "../../../components/MainPageContainer";
import { FilterTab } from "../../../components/Filters";
import DownloadButton from "../../../components/DownloadButton";
import Tooltip from "../../../components/tooltip";
import { SearchBox } from "../../../components/Search";
import { ChartTable } from "../../../components/table/ChartTable";
import { downloadChartTableCSV } from "../../../components/table/csvExport";
import {
  getJvvnlCircles,
  getJvvnlTranscoSubstationsList,
  getJvvnlDiscomSubstations,
  getJvvnlFeeders,
  getJvvnlDigitalTwin,
} from "../../../Rest_api/RestApi";
import type { JvvnlDtCircle } from "../../../Rest_api/RestApi";

const MapView = lazy(() => import("./MapView"));

type PillOption = { value: string | number; label: string };

type DtRow = {
  circle_name: string;
  transco_substation_id: number;
  transco_substation_name: string;
  transco_key_name: string;
  district: string;
  divisional_headquarter: string;
  discom: string;
  transco_location_name: string;
  transco_latitude: number | string;
  transco_longitude: number | string;
  discom_substation_id: number;
  discom_substation_name: string;
  feeder_id: number;
  feeder_name: string;
  feeder_number: string;
  feeder_location_name: string;
  feeder_latitude: number | string;
  feeder_longitude: number | string;
  generators: string;
};

const DataTabs = [{ label: "Map" }, { label: "Table" }];

// All toggleable table columns (key → header label), in display order.
const ALL_COLUMNS: { key: string; label: string }[] = [
  { key: "circle_name", label: "Circle" },
  { key: "transco_substation_name", label: "Transco S/S" },
  { key: "district", label: "District" },
  { key: "divisional_headquarter", label: "Divisional HQ" },
  { key: "discom", label: "Discom" },
  { key: "transco_location_name", label: "Transco Location" },
  { key: "transco_latitude", label: "Transco Lat" },
  { key: "transco_longitude", label: "Transco Long" },
  { key: "discom_substation_name", label: "Discom S/S" },
  { key: "feeder_name", label: "Feeder" },
  { key: "feeder_number", label: "Feeder No." },
  { key: "feeder_location_name", label: "Feeder Location" },
  { key: "feeder_latitude", label: "Feeder Lat" },
  { key: "feeder_longitude", label: "Feeder Long" },
  { key: "generators", label: "Generators" },
];

const DgitalTwinPage = () => {
  const [searchParams] = useSearchParams();

  const [dataTab, setDataTab] = useState(DataTabs[0]);

  // ── Net-consumption entity data (ported from JvvnlMetric) ──────────────
  const [allDiscomSubs, setAllDiscomSubs] = useState<any[]>([]);
  const [allFeeders, setAllFeeders] = useState<any[]>([]);
  const [allTranscos, setAllTranscos] = useState<PillOption[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const [circleList, setCircleList] = useState<PillOption[]>([]);
  // Transco ids belonging to the selected circle (A2 cascade). null = unfiltered.
  const [circleTranscoIds, setCircleTranscoIds] = useState<Set<number> | null>(
    null,
  );
  // transco_substation_id → circle PillOption (for reverse lookup)
  const [transcoIdToCircle, setTranscoIdToCircle] = useState<
    Map<number, PillOption>
  >(new Map());

  const [circle, setCircle] = useState<PillOption | null>(() => {
    const v = searchParams.get("circle");
    const l = searchParams.get("circle_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });
  const [transco, setTransco] = useState<PillOption | null>(() => {
    const v = searchParams.get("transco");
    const l = searchParams.get("transco_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });
  const [discom, setDiscom] = useState<PillOption | null>(() => {
    const v = searchParams.get("discom");
    const l = searchParams.get("discom_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });
  const [feeder, setFeeder] = useState<PillOption | null>(() => {
    const v = searchParams.get("feeder");
    const l = searchParams.get("feeder_label");
    return v ? { value: Number(v), label: l ?? v } : null;
  });

  // Load circle options from API; also build transcoId→circle reverse map.
  useEffect(() => {
    let mounted = true;
    getJvvnlCircles()
      .then(async (circles) => {
        if (!mounted) return;
        const opts: PillOption[] = circles.map((c) => ({
          value: c.circle_id,
          label: c.circle_name,
        }));
        setCircleList(opts);
        setCircle((prev) => prev ?? null);
        // Build reverse map: fetch all transcos for all circles in parallel
        const entries = await Promise.all(
          opts.map((c) =>
            getJvvnlTranscoSubstationsList(Number(c.value))
              .then((res) =>
                res.results.map(
                  (r) => [Number(r.id), c] as [number, PillOption],
                ),
              )
              .catch(() => [] as [number, PillOption][]),
          ),
        );
        if (!mounted) return;
        setTranscoIdToCircle(new Map(entries.flat()));
      })
      .catch(() => mounted && setCircleList([]));
    return () => {
      mounted = false;
    };
  }, []);

  // A2 cascade: fetch the transco ids that belong to the selected circle.
  useEffect(() => {
    const circleId = circle ? Number(circle.value) : undefined;
    if (!circleId || isNaN(circleId)) {
      setCircleTranscoIds(null);
      return;
    }
    let mounted = true;
    getJvvnlTranscoSubstationsList(circleId)
      .then((res) => {
        if (!mounted) return;
        setCircleTranscoIds(
          new Set(
            res.results.map((r) => Number(r.id)).filter((n) => !isNaN(n)),
          ),
        );
      })
      .catch(() => mounted && setCircleTranscoIds(null));
    return () => {
      mounted = false;
    };
  }, [circle]);

  // Transco list scoped to the selected circle (A2). Falls back to all transcos
  // when the scope set is unavailable.
  const transcoList: PillOption[] = useMemo(() => {
    if (!circleTranscoIds) return allTranscos;
    return allTranscos.filter((t) => circleTranscoIds.has(Number(t.value)));
  }, [allTranscos, circleTranscoIds]);

  // Discom list filtered by selected transco (parent → child)
  const discomList: PillOption[] = useMemo(() => {
    const filtered = transco
      ? allDiscomSubs.filter((d) => d.transco_substation_id === transco.value)
      : circleTranscoIds
        ? allDiscomSubs.filter((d) =>
            circleTranscoIds.has(Number(d.transco_substation_id)),
          )
        : allDiscomSubs;
    return filtered.map((d) => ({
      value: d.discom_substation_id,
      label: d.discom_substation_name,
    }));
  }, [allDiscomSubs, transco, circleTranscoIds]);

  // Feeder list filtered by selected discom, or by selected transco's discoms
  const feederList: PillOption[] = useMemo(() => {
    let filtered = allFeeders;
    if (discom) {
      filtered = allFeeders.filter(
        (f) => f.discom_substation_id === discom.value,
      );
    } else if (transco) {
      const discomIds = new Set(
        allDiscomSubs
          .filter((d) => d.transco_substation_id === transco.value)
          .map((d) => d.discom_substation_id),
      );
      filtered = allFeeders.filter((f) =>
        discomIds.has(f.discom_substation_id),
      );
    } else if (circleTranscoIds) {
      const discomIds = new Set(
        allDiscomSubs
          .filter((d) => circleTranscoIds.has(Number(d.transco_substation_id)))
          .map((d) => d.discom_substation_id),
      );
      filtered = allFeeders.filter((f) =>
        discomIds.has(f.discom_substation_id),
      );
    }
    return filtered.map((f) => ({
      value: f.feeder_id,
      label: f.feeder_name || String(f.feeder_id),
    }));
  }, [allFeeders, allDiscomSubs, discom, transco, circleTranscoIds]);

  // ── Digital-twin topology (for the Table tab) ──────────────────────────
  const [dtTree, setDtTree] = useState<JvvnlDtCircle[]>([]);
  const [dtLoading, setDtLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setDtLoading(true);
    getJvvnlDigitalTwin()
      .then((tree) => mounted && setDtTree(tree))
      .finally(() => mounted && setDtLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Flatten tree → one row per feeder.
  const dtRows: DtRow[] = useMemo(() => {
    const rows: DtRow[] = [];
    for (const c of dtTree) {
      for (const t of c.transco_substations ?? []) {
        for (const d of t.discom_substations ?? []) {
          const generators = (d.generators ?? [])
            .map(
              (g) =>
                `${g.plant_name} (${g.plant_type}, ${g.capacity_mw ?? "-"} MW)`,
            )
            .join("; ");
          for (const f of d.feeders ?? []) {
            rows.push({
              circle_name: c.circle_name,
              transco_substation_id: t.transco_substation_id,
              transco_substation_name: t.transco_substation_name,
              transco_key_name: t.key_name ?? "",
              district: t.district ?? "",
              divisional_headquarter: t.divisional_headquarter ?? "",
              discom: t.discom ?? "",
              transco_location_name: t.location_name ?? "",
              transco_latitude: t.latitude ?? "",
              transco_longitude: t.longitude ?? "",
              discom_substation_id: d.discom_substation_id,
              discom_substation_name: d.discom_substation_name,
              feeder_id: f.feeder_id,
              feeder_name: f.feeder_name,
              feeder_number: f.feeder_number ?? "",
              feeder_location_name: f.location_name ?? "",
              feeder_latitude: f.latitude ?? "",
              feeder_longitude: f.longitude ?? "",
              generators,
            });
          }
        }
      }
    }
    return rows;
  }, [dtTree]);

  // Filter rows by the active searchbox selection (most specific wins).
  const filteredRows: DtRow[] = useMemo(() => {
    return dtRows.filter((r) => {
      if (feeder && r.feeder_id !== feeder.value) return false;
      if (discom && r.discom_substation_id !== discom.value) return false;
      if (transco && r.transco_substation_id !== transco.value) return false;
      if (circle && r.circle_name !== circle.label) return false;
      return true;
    });
  }, [dtRows, feeder, discom, transco, circle]);

  // Most-specific search selection -> namespaced node id for the map to reveal.
  const searchRevealId = useMemo(() => {
    if (feeder) return `feeder:${feeder.value}`;
    if (discom) return `discom:${discom.value}`;
    if (transco) return `transco:${transco.value}`;
    if (circle) return `circle:${circle.value}`;
    return null;
  }, [feeder, discom, transco, circle]);

  // Date range for the map's live metrics. The load-summary API returns count:0
  // for "today" on QA (data doesn't reach the current day), so we reuse the
  // backend-seeded range every other JVVNL page uses: defaultDates[tab].
  // defaultRangeDate. Pick the first seeded tab that has a range; fall back to a
  // recent 1-day window (yesterday) until the slice is seeded.
  const defaultDates = useSelector(
    (state: any) => state.defaultDatesSetter?.defaultDates,
  );
  const dateRange = useMemo(() => {
    const tabs = defaultDates ? Object.values(defaultDates) : [];
    for (const t of tabs as any[]) {
      const r = t?.defaultRangeDate;
      if (r?.startDate && r?.endDate) {
        return {
          startDate: formatDateForAPI(new Date(r.startDate)),
          endDate: formatDateForAPI(new Date(r.endDate)),
        };
      }
    }
    // Fallback: yesterday (today often has no ingested data yet).
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const ymd = formatDateForAPI(d);
    return { startDate: ymd, endDate: ymd };
  }, [defaultDates]);

  const [cpActive, setCpActive] = useState(false);

  // Column visibility — start with all columns shown.
  const [visibleCols, setVisibleCols] = useState<Set<string>>(
    () => new Set(ALL_COLUMNS.map((c) => c.key)),
  );

  // Options + value for the column-toggle SearchBox (value = column key).
  const colOptions: PillOption[] = useMemo(
    () => ALL_COLUMNS.map((c) => ({ value: c.key, label: c.label })),
    [],
  );
  const colSelected: PillOption[] = useMemo(
    () => colOptions.filter((o) => visibleCols.has(String(o.value))),
    [colOptions, visibleCols],
  );

  // ChartTable params: text columns via extraKeys, generator count as a legend.
  // Only the visible columns are passed through (display order preserved).
  const tablePar = useMemo(() => {
    const shown = ALL_COLUMNS.filter((c) => visibleCols.has(c.key));
    return {
      data: filteredRows,
      legends: [],
      extraKeys: shown.map((c) => c.key),
      extraLabels: shown.map((c) => c.label),
      unit: "",
      filename: "digital-twin",
      showTotal: false,
      isLoading: dtLoading,
    };
  }, [filteredRows, dtLoading, visibleCols]);

  // Load discom substations + feeders once; build transco list from discoms.
  useEffect(() => {
    if (allDiscomSubs.length > 0 && allFeeders.length > 0) return;

    let mounted = true;
    setLoadingEntities(true);
    Promise.all([
      allDiscomSubs.length === 0
        ? getJvvnlDiscomSubstations().catch(() => [])
        : Promise.resolve(allDiscomSubs),
      allFeeders.length === 0
        ? getJvvnlFeeders().catch(() => [])
        : Promise.resolve(allFeeders),
    ])
      .then(([discoms, feeders]) => {
        if (!mounted) return;
        setAllDiscomSubs(discoms);
        setAllFeeders(feeders);
        const seen = new Set<number>();
        const transcos: PillOption[] = [];
        for (const d of discoms as any[]) {
          if (d.transco_substation_id && !seen.has(d.transco_substation_id)) {
            seen.add(d.transco_substation_id);
            transcos.push({
              value: d.transco_substation_id,
              label: d.transco_substation_name,
            });
          }
        }
        setAllTranscos(transcos);
      })
      .finally(() => mounted && setLoadingEntities(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="gas-page-container">
        <MainPageContainer className="!w-[calc(100vw-105px)]">
          <div className="gas-chart-container flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="flex flex-col border-gray-200 bg-white shrink-0">
              <div
                className="gas-heading"
                style={{
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: "8px",
                }}
              >
                <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                  <h2 className="chartHeading shrink-0">Grid Replica</h2>
                  <div className="flex items-center gap-x-3">
                    <FilterTab
                      options={DataTabs.map((item) => item.label)}
                      selected={[dataTab.label]}
                      onClick={(_, index) => setDataTab(DataTabs[index])}
                      variant="pill-outline"
                    />
                    {/* <DownloadButton
                      onClick={() => downloadChartTableCSV(tablePar)}
                    /> */}
                  </div>
                </div>

                {/* Circle › Transco S/S › Discom S/S › Feeder (parent-child) */}
                <div className="flex items-center gap-2 pb-1">
                  <SearchBox
                    sortAlphabetically={true}
                    options={circleList}
                    selectedOptions={circle ? [circle] : []}
                    totalCount={circleList.length}
                    onChange={(o: any) => {
                      setCircle(o);
                      setTransco(null);
                      setDiscom(null);
                      setFeeder(null);
                    }}
                    placeholder="Search circle..."
                    label="Circle"
                    minWidth={160}
                    isLoading={loadingEntities}
                    isClearable
                  />
                  <span className="text-gray-400 text-base font-medium select-none px-0.5">
                    ›
                  </span>
                  <SearchBox
                    sortAlphabetically={true}
                    options={transcoList}
                    selectedOptions={transco ? [transco] : []}
                    totalCount={transcoList.length}
                    onChange={(o: any) => {
                      setTransco(o);
                      setDiscom(null);
                      setFeeder(null);
                      // backward map: auto-fill circle
                      if (o) {
                        const c = transcoIdToCircle.get(Number(o.value));
                        if (c) setCircle(c);
                      }
                    }}
                    placeholder="Search transco substation..."
                    label="Transco Substation"
                    width={200}
                    minWidth={190}
                    isLoading={loadingEntities}
                    isClearable
                  />
                  <span className="text-gray-400 text-base font-medium select-none px-0.5">
                    ›
                  </span>
                  <SearchBox
                    sortAlphabetically={true}
                    options={discomList}
                    selectedOptions={discom ? [discom] : []}
                    totalCount={discomList.length}
                    onChange={(o: any) => {
                      setDiscom(o);
                      setFeeder(null);
                      // backward map: auto-fill transco
                      if (o) {
                        const raw = allDiscomSubs.find(
                          (d) => d.discom_substation_id === o.value,
                        );
                        if (raw?.transco_substation_id) {
                          const t = allTranscos.find(
                            (x) => x.value === raw.transco_substation_id,
                          );
                          if (t) setTransco(t);
                          const c = transcoIdToCircle.get(
                            Number(raw.transco_substation_id),
                          );
                          if (c) setCircle(c);
                        }
                      }
                    }}
                    placeholder="Search discom substation..."
                    label="Discom Substation"
                    width={200}
                    minWidth={190}
                    isLoading={loadingEntities}
                    isClearable
                  />
                  <span className="text-gray-400 text-base font-medium select-none px-0.5">
                    ›
                  </span>
                  <SearchBox
                    sortAlphabetically={true}
                    options={feederList}
                    totalCount={feederList.length}
                    selectedOptions={feeder ? [feeder] : []}
                    onChange={(o: any) => {
                      setFeeder(o);
                      // backward map: auto-fill discom + transco
                      if (o) {
                        const rawF = allFeeders.find(
                          (f) => f.feeder_id === o.value,
                        );
                        if (rawF?.discom_substation_id) {
                          const rawD = allDiscomSubs.find(
                            (d) =>
                              d.discom_substation_id ===
                              rawF.discom_substation_id,
                          );
                          if (rawD) {
                            setDiscom({
                              value: rawD.discom_substation_id,
                              label: rawD.discom_substation_name,
                            });
                            if (rawD.transco_substation_id) {
                              const t = allTranscos.find(
                                (x) => x.value === rawD.transco_substation_id,
                              );
                              if (t) setTransco(t);
                              const c = transcoIdToCircle.get(
                                Number(rawD.transco_substation_id),
                              );
                              if (c) setCircle(c);
                            }
                          }
                        }
                      }
                    }}
                    placeholder="Search feeder..."
                    label="Feeder"
                    minWidth={160}
                    isLoading={loadingEntities}
                    isClearable
                  />
                  {dataTab.label === "Table" && (
                    <div className="ml-auto">
                      <SearchBox
                        isMany
                        options={colOptions}
                        selectedOptions={colSelected}
                        totalCount={colOptions.length}
                        onChange={(opts: any) => {
                          setVisibleCols(
                            new Set<string>(
                              (opts ?? []).map((o: PillOption) =>
                                String(o.value),
                              ),
                            ),
                          );
                        }}
                        enableSelectAll
                        selectAllLabel="Show all"
                        clearAllLabel="Hide all"
                        placeholder="Columns..."
                        label="Columns"
                        width={170}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {dataTab.label === "Map" && (
              <div className="flex-1 min-h-0">
                <Suspense
                  fallback={
                    <div className="p-4 text-gray-500">Loading map…</div>
                  }
                >
                  <MapView
                    tree={dtTree}
                    searchRevealId={searchRevealId}
                    dateRange={dateRange}
                  />
                </Suspense>
              </div>
            )}
            {dataTab.label === "Table" && (
              <div className="flex-1 min-h-0">
                <ChartTable
                  {...tablePar}
                  tableHeight="100%"
                  hideCriticalPeriodsButton={true}
                  cpActive={cpActive}
                  onCpActiveChange={setCpActive}
                />
              </div>
            )}
          </div>

          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5">
              <Tooltip
                note={<p>Source:</p>}
                text="1. Weather : Open-Meteo&#10;2. Load : Discom&#10;3. Feeder : AMISP&#10;4. Generation : AMISP (HKRP)&#10;"
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
    </>
  );
};

export default DgitalTwinPage;
